import { motion } from "framer-motion";
import { zTeenImg, webDev } from "../../assets";
import Button from "../../components/button/button";
import Layout from "../../components/shared/layout/layout";

import MonthSuperstar from "./components/month-superstar";
import ReadyToJoin from "./components/ready-to-join";
import StudentLedProject from "./components/student-led-projects";
import UpcomingEvents from "./components/upcoming-event";
import WhatsIsZTeens from "./components/whatsIsTeen";
import WhyZTeens from "./components/why-z-teens";
import WhatTeensCard from "./components/what-teens-card";
import teens4 from "../../assets/videos/teens4.mp4";

const stats = [
  { number: "01", label: "Young Innovators" },
  { number: "02", label: "Build Real Projects" },
  { number: "03", label: "Learn From Experts" },
  { number: "∞", label: "Possibilities" },
];

const pillars = [
  {
    number: "01",
    title: "BUILD",
    description:
      "Move beyond theory. Build websites, apps, games, digital products and technology solutions that actually work.",
  },
  {
    number: "02",
    title: "CREATE",
    description:
      "Turn imagination into something tangible through coding, robotics, design, AI and creative technology.",
  },
  {
    number: "03",
    title: "CONNECT",
    description:
      "Learn alongside other ambitious teenagers, mentors, engineers and technology professionals.",
  },
  {
    number: "04",
    title: "LEAD",
    description:
      "Develop the confidence, communication and problem-solving skills needed to become tomorrow's technology leaders.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: "easeOut",
    },
  },
};

const ZTEENS = () => {
  return (
    <Layout>
      <div className="overflow-hidden bg-white">

        {/* =====================================================
            HERO
        ====================================================== */}

        <section className="relative min-h-[720px] flex items-center overflow-hidden">

          {/* Background */}
          <div className="absolute inset-0 bg-[#06131a]" />

          {/* Glow */}
          <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-[#59b5cd]/20 rounded-full blur-[120px]" />
          <div className="absolute top-1/3 -right-40 w-[500px] h-[500px] bg-[#ecb32c]/15 rounded-full blur-[130px]" />
          <div className="absolute bottom-0 left-1/3 w-[400px] h-[300px] bg-[#cf375d]/10 rounded-full blur-[100px]" />

          {/* Grid */}
          <div
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage:
                "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
              backgroundSize: "50px 50px",
            }}
          />

          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full py-20">

            <div className="grid lg:grid-cols-2 gap-14 items-center">

              {/* LEFT */}
              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="visible"
              >

                {/* Badge */}
                <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-xl text-white text-sm mb-7">
                  <span className="w-2 h-2 rounded-full bg-[#ecb32c] animate-pulse" />
                  TEENS TECHNOLOGY PROGRAM
                </div>

                <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-[0.95] tracking-tight">
                  Don't just
                  <span className="block text-[#59b5cd]">
                    use technology.
                  </span>

                  <span className="block">
                    <span className="text-[#ecb32c]">Build</span> it.
                  </span>
                </h1>

                <p className="mt-8 text-lg sm:text-xl text-gray-300 max-w-xl leading-relaxed">
                  Our tech4Teens is where young minds learn to code, create,
                  experiment and build the technology of tomorrow.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 mt-9">

                  <a
                    href="https://wa.me/2348115371014?text=Hello Zulfah Team, I’m interested in the Tech4Teens programme and would love to help my teenager develop practical technology skills for the future. Please share more information about the programme, courses, schedule, fees, and enrolment process "
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button
                      btnText="Join"
                      btnClassName="!bg-[#ecb32c] !text-[#06131a] px-8 py-4 rounded-xl font-bold text-base hover:scale-105 transition-all shadow-[0_10px_40px_rgba(236,179,44,0.25)]"
                    />
                  </a>

                  <a href="#experience">
                    <button className="px-8 py-4 rounded-xl border border-white/15 bg-white/5 backdrop-blur-xl text-white font-semibold hover:bg-white/10 transition">
                      Explore the Experience ↓
                    </button>
                  </a>

                </div>

                {/* Mini trust */}
                <div className="flex items-center gap-6 mt-10 text-sm text-gray-400">
                  <span>✓ Beginner Friendly</span>
                  <span>✓ Practical Projects</span>
                  <span>✓ Mentorship</span>
                </div>

              </motion.div>

              {/* RIGHT */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9, x: 40 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                transition={{ duration: 0.9 }}
                className="relative"
              >

                {/* Image glow */}
                <div className="absolute inset-10 bg-[#59b5cd]/20 blur-[80px] rounded-full" />

                <div className="relative rounded-[2rem] overflow-hidden border border-white/10 shadow-2xl">

                  <img
                    src={zTeenImg}
                    alt="tech4Teens"
                    className="w-full object-cover"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-[#06131a]/80 via-transparent to-transparent" />

                  {/* Floating Card */}
                  <motion.div
                    animate={{ y: [0, -10, 0] }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    className="absolute bottom-6 left-6 right-6 p-5 rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20 text-white"
                  >
                    <div className="flex justify-between items-center">

                      <div>
                        <p className="text-xs text-gray-300 uppercase tracking-widest">
                          Future in progress
                        </p>
                        <p className="font-bold text-lg mt-1">
                          Young. Curious. Technical.
                        </p>
                      </div>

                      <div className="w-11 h-11 rounded-full bg-[#ecb32c] flex items-center justify-center text-[#06131a] font-black">
                        →
                      </div>

                    </div>
                  </motion.div>

                </div>

                {/* Floating Number */}
                <motion.div
                  animate={{ rotate: [0, 4, -4, 0] }}
                  transition={{ duration: 6, repeat: Infinity }}
                  className="absolute -top-5 -right-5 w-24 h-24 rounded-2xl bg-[#ecb32c] flex flex-col items-center justify-center shadow-xl rotate-6"
                >
                  <span className="text-3xl font-black text-[#06131a]">
                    ∞
                  </span>
                  <span className="text-[10px] uppercase font-bold">
                    Possibilities
                  </span>
                </motion.div>

              </motion.div>

            </div>
          </div>
        </section>

        {/* =====================================================
            INTRO
        ====================================================== */}

        <section className="py-24 px-6">
          <div className="max-w-7xl mx-auto">

            <motion.div
              variants={fadeUp}
              whileInView="visible"
              initial="hidden"
              viewport={{ once: true }}
              className="grid lg:grid-cols-[1fr_1.5fr] gap-12 items-end"
            >

              <div>
                <p className="text-[#ecb32c] font-bold uppercase tracking-[0.2em] text-sm mb-4">
                  Welcome to tech4Teens
                </p>

                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#07151c] leading-tight">
                  The earlier they
                  <span className="text-[#59b5cd]"> start,</span>
                  <br />
                  the further they
                  <span className="text-[#ecb32c]"> go.</span>
                </h2>
              </div>

              <p className="text-xl text-gray-600 leading-relaxed max-w-2xl">
                Technology is no longer just a career path. It is the language
                of the future. tech4Teens gives secondary school students the
                opportunity to discover that language early  through practical
                learning, mentorship, collaboration and real projects.
              </p>

            </motion.div>

            <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 border-y border-gray-200">

              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="py-10 px-6 border-r last:border-r-0 border-gray-200"
                >
                  <p className="text-4xl sm:text-5xl font-black text-[#07151c]">
                    {stat.number}
                  </p>

                  <p className="text-gray-500 mt-2">
                    {stat.label}
                  </p>
                </motion.div>
              ))}

            </div>

          </div>
        </section>

        {/* =====================================================
            WHAT IS ZTEENS
        ====================================================== */}

        <section className="bg-[#f7f9fa] py-20 px-6">
          <WhatsIsZTeens />
        </section>

        {/* =====================================================
            PILLARS
        ====================================================== */}

        <section className="bg-[#06131a] py-24 px-6 text-white">

          <div className="max-w-7xl mx-auto">

            <div className="max-w-2xl mb-16">
              <p className="text-[#ecb32c] font-bold uppercase tracking-[0.2em] text-sm">
                The Philosophy
              </p>

              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black mt-4 leading-tight">
                We don't just teach
                <span className="text-[#59b5cd]"> technology.</span>
              </h2>

              <p className="text-gray-400 text-lg mt-6">
                We develop young people who are confident enough to imagine
                what's next — and skilled enough to build it.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 border-t border-white/10">

              {pillars.map((pillar, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group p-8 border-r border-b border-white/10 hover:bg-white/[0.03] transition"
                >

                  <span className="text-[#ecb32c] text-sm font-bold">
                    {pillar.number}
                  </span>

                  <h3 className="text-3xl font-black mt-10 group-hover:text-[#59b5cd] transition">
                    {pillar.title}
                  </h3>

                  <p className="text-gray-400 mt-5 leading-relaxed">
                    {pillar.description}
                  </p>

                  <div className="mt-8 w-10 h-[2px] bg-[#ecb32c] group-hover:w-20 transition-all" />

                </motion.div>
              ))}

            </div>

          </div>
        </section>

        {/* =====================================================
            WHY ZTEENS
        ====================================================== */}

        <section className="py-24 px-6">
          <div className="max-w-7xl mx-auto">
            <WhyZTeens />
          </div>
        </section>

        {/* =====================================================
            EXPERIENCE
        ====================================================== */}

  <section
  id="experience"
  className="relative overflow-hidden py-28 px-6 bg-[#f7f9fa]"
>
  {/* =========================================================
      BACKGROUND
  ========================================================== */}

  <div className="absolute inset-0 pointer-events-none overflow-hidden">

    {/* Grid */}
    <div
      className="absolute inset-0 opacity-[0.035]"
      style={{
        backgroundImage:
          "linear-gradient(#07151c 1px, transparent 1px), linear-gradient(90deg, #07151c 1px, transparent 1px)",
        backgroundSize: "50px 50px",
      }}
    />

    {/* Glow 1 */}
    <motion.div
      className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#59b5cd]/20 blur-[120px]"
      animate={{
        scale: [1, 1.15, 1],
        x: [0, 30, 0],
        y: [0, 20, 0],
      }}
      transition={{
        duration: 10,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />

    {/* Glow 2 */}
    <motion.div
      className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#ecb32c]/20 blur-[120px]"
      animate={{
        scale: [1, 1.2, 1],
        x: [0, -30, 0],
      }}
      transition={{
        duration: 12,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />

  </div>

  <div className="relative max-w-7xl mx-auto">

    {/* =========================================================
        HEADER
    ========================================================== */}

    <div className="grid lg:grid-cols-[1fr_1.5fr] gap-10 items-end mb-20">

      <div>

        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="w-10 h-[2px] bg-[#cf375d]" />

          <span className="text-[#cf375d] font-bold uppercase tracking-[0.25em] text-xs">
            The tech4Teens Experience
          </span>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95] text-[#07151c]"
        >
          Don't just
          <br />

          <span className="text-[#59b5cd]">
            learn.
          </span>

          <br />

          <span className="relative inline-block">

            Build.

            <motion.span
              className="absolute -bottom-2 left-0 h-2 bg-[#ecb32c] rounded-full"
              initial={{ width: 0 }}
              whileInView={{ width: "70%" }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
            />

          </span>
        </motion.h2>

      </div>


      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="lg:max-w-2xl"
      >

        <p className="text-xl sm:text-2xl leading-relaxed text-gray-600">
          At tech4Teens, learning isn't a spectator sport.
          <span className="text-[#07151c] font-semibold">
            {" "}Ideas become experiments. Experiments become projects.
          </span>
          {" "}And projects become the beginning of something bigger.
        </p>

      </motion.div>

    </div>


    {/* =========================================================
        FEATURED VIDEO
    ========================================================== */}

    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
      className="relative mb-24"
    >

      {/* Floating label */}

      <motion.div
        className="absolute -top-6 left-6 sm:left-10 z-20 bg-[#07151c] text-white px-5 py-3 rounded-full shadow-xl"
        initial={{ opacity: 0, y: -15 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.4 }}
      >
        <span className="text-xs font-bold tracking-widest uppercase">
          Inside tech4Teens
        </span>
      </motion.div>


      {/* Decorative corner */}

      <div className="absolute -top-5 -right-5 w-20 h-20 border-t-4 border-r-4 border-[#ecb32c] rounded-tr-3xl hidden sm:block" />

      <div className="absolute -bottom-5 -left-5 w-20 h-20 border-b-4 border-l-4 border-[#59b5cd] rounded-bl-3xl hidden sm:block" />


      <div className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_80px_rgba(7,21,28,0.18)] bg-black">

        <video
          className="w-full aspect-video object-cover"
          controls
          poster={zTeenImg}
        >
          <source src={teens4} type="video/mp4" />
        </video>

        {/* Overlay */}

        <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/30 via-transparent to-transparent" />

      </div>

    </motion.div>


    {/* =========================================================
        JOURNEY HEADER
    ========================================================== */}

    <div className="text-center mb-14">

      <p className="text-[#59b5cd] font-bold uppercase tracking-[0.2em] text-xs mb-3">
        How We Do It
      </p>

      <h3 className="text-3xl sm:text-4xl font-black text-[#07151c]">
        From curiosity to creation.
      </h3>

    </div>


    {/* =========================================================
        EXPERIENCE JOURNEY
    ========================================================== */}

    <div className="relative">

      {/* Connecting line */}

      <div className="hidden lg:block absolute top-[75px] left-[12%] right-[12%] h-[2px] bg-gradient-to-r from-[#59b5cd] via-[#ecb32c] to-[#cf375d]" />


      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">

        {[
          {
            number: "01",
            title: "Explore",
            label: "Curiosity",
            description:
              "Discover coding, robotics, AI, design and technology through exciting hands-on experiences.",
            color: "#59b5cd",
            icon: "⌁",
          },
          {
            number: "02",
            title: "Experiment",
            label: "Discovery",
            description:
              "Break things. Try things. Ask questions. Learn by experimenting in a safe environment.",
            color: "#ecb32c",
            icon: "✦",
          },
          {
            number: "03",
            title: "Build",
            label: "Creation",
            description:
              "Turn ideas into websites, apps, robots and digital products that actually work.",
            color: "#cf375d",
            icon: "⌘",
          },
          {
            number: "04",
            title: "Impact",
            label: "Possibility",
            description:
              "Present projects, collaborate with others and discover how technology can solve real problems.",
            color: "#59b5cd",
            icon: "↗",
          },
        ].map((item, index) => (

          <motion.div
            key={item.number}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{
              duration: 0.6,
              delay: index * 0.12,
            }}
            whileHover={{ y: -10 }}
            className="group relative"
          >

            {/* Number */}

            <div className="relative z-10 flex justify-center mb-7">

              <motion.div
                whileHover={{ rotate: 10, scale: 1.1 }}
                className="w-[76px] h-[76px] rounded-full bg-white border-4 flex items-center justify-center shadow-xl"
                style={{
                  borderColor: item.color,
                }}
              >

                <span
                  className="text-2xl font-black"
                  style={{ color: item.color }}
                >
                  {item.icon}
                </span>

              </motion.div>

            </div>


            {/* Card */}

            <div className="relative bg-white rounded-3xl p-7 border border-gray-100 shadow-sm group-hover:shadow-2xl transition-all duration-500 overflow-hidden">

              {/* Top accent */}

              <div
                className="absolute top-0 left-0 w-full h-1"
                style={{ backgroundColor: item.color }}
              />

              {/* Number */}

              <span className="text-xs font-black tracking-widest text-gray-300">
                {item.number}
              </span>

              <p
                className="text-xs font-bold uppercase tracking-widest mt-4 mb-2"
                style={{ color: item.color }}
              >
                {item.label}
              </p>

              <h4 className="text-2xl font-black text-[#07151c] mb-3">
                {item.title}
              </h4>

              <p className="text-gray-600 leading-relaxed text-sm">
                {item.description}
              </p>


              {/* Hover arrow */}

              <motion.div
                className="mt-6 font-bold text-sm flex items-center gap-2"
                initial={{ opacity: 0.5 }}
                whileHover={{ opacity: 1 }}
                style={{ color: item.color }}
              >
                Discover
                <motion.span
                  className="inline-block"
                  whileHover={{ x: 5 }}
                >
                  →
                </motion.span>
              </motion.div>


              {/* Decorative shape */}

              <div
                className="absolute -bottom-10 -right-10 w-24 h-24 rounded-full opacity-[0.06]"
                style={{ backgroundColor: item.color }}
              />

            </div>

          </motion.div>

        ))}

      </div>

    </div>


    {/* =========================================================
        BOTTOM STATEMENT
    ========================================================== */}

    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      className="relative mt-24 overflow-hidden rounded-[2rem] bg-[#07151c] px-8 sm:px-14 py-12 sm:py-16"
    >

      {/* Background typography */}

      <div className="absolute right-[-20px] top-[-40px] text-[180px] sm:text-[240px] font-black text-white/[0.025] leading-none select-none">
        Z
      </div>


      <div className="relative z-10 grid md:grid-cols-[1fr_auto] gap-10 items-center">

        <div>

          <p className="text-[#ecb32c] text-xs font-bold uppercase tracking-[0.25em] mb-4">
            The goal
          </p>

          <h3 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight max-w-3xl">
            We aren't preparing teens
            <span className="text-[#59b5cd]"> just to use technology.</span>
            <br />
            We're preparing them to shape it.
          </h3>

        </div>


        <motion.div
          whileHover={{ scale: 1.05, rotate: 2 }}
          className="w-24 h-24 sm:w-32 sm:h-32 rounded-full border border-[#59b5cd]/40 flex items-center justify-center bg-white/5 backdrop-blur-sm"
        >

          <span className="text-4xl">
            🚀
          </span>

        </motion.div>

      </div>

    </motion.div>

  </div>

</section>

        {/* =====================================================
            STUDENT PROJECTS
        ====================================================== */}

        <section className="py-24 px-6">
          <div className="max-w-7xl mx-auto">
            <StudentLedProject />
          </div>
        </section>

      

  

        {/* =====================================================
            FINAL CTA
        ====================================================== */}

        <section className="relative overflow-hidden bg-[#ecb32c] py-28 px-6">

          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-white/20 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#59b5cd]/20 blur-3xl" />

          <div className="relative max-w-5xl mx-auto text-center">

            <p className="uppercase tracking-[0.25em] font-black text-[#06131a]/60 text-sm">
              Their future starts now
            </p>

            <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black text-[#06131a] mt-5 leading-[0.95]">
              Give them the
              <br />
              <span className="text-white">power to build.</span>
            </h2>

            <p className="text-[#06131a]/70 text-lg sm:text-xl max-w-2xl mx-auto mt-7">
              Don't wait for the future to arrive. Help your child start
              building it today.
            </p>

            <a
              href="https://docs.google.com/forms/d/e/1FAIpQLSfUuvrbrRAHm-fp1O0cJlnlSO21akZDgR7Kt3i1piufsdJi_g/viewform"
              target="_blank"
              rel="noreferrer"
            >
              <Button
                btnText="Join Today →"
                btnClassName="!bg-[#06131a] !text-white  w-[50%] px-10 py-5 rounded-2xl font-bold text-lg mt-9 hover:scale-105 transition-all shadow-2xl"
              />
            </a>

          </div>

        </section>

   

      </div>
    </Layout>
  );
};

export default ZTEENS;